import {useCallback, Fragment, useEffect, useState} from 'react';
import {Image, Link, useServerProps} from '@shopify/hydrogen';
import {FocusTrap} from '@headlessui/react';
import useEmblaCarousel from 'embla-carousel-react';
import logo from '../assets/image/logo-el-librero.svg';
import arrowLeft from '../assets/image/arrow-left.svg';
import MobileCountrySelector from './MobileCountrySelector.client';
import OpenIcon from '../assets/image/mobileicon.svg';
import {getPath} from '../lib/methods';

let scrollPosition = 0;

/**
 * A client component that defines the navigation for a mobile storefront
 */
export default function MobileNavigation({menu, isOpen, setIsOpen}) {
  const [viewportRef, embla] = useEmblaCarousel({
    containScroll: 'scrollSnap',
    slidesToScroll: 1,
    inViewThreshold: 1,
    skipSnaps: false,
    selectedClass: '',
    dragFree: true,
    align: 'start',
    loop: false,
  });

  useServerProps;
  const {pending} = useServerProps();
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const scrollPrev = useCallback(() => embla && embla.scrollPrev(), [embla]);
  const scrollNext = useCallback(() => embla && embla.scrollNext(), [embla]);

  const OpenFocusTrap = isOpen ? FocusTrap : Fragment;
  const [selectedTitle, setTitle] = useState(false);

  const PrevButton = ({enabled, onClick}) => (
    <button
      className="hidden absolute left-0  top-2/4 bottom-52 items-center justify-center rounded-full h-10 w-10 bg-white cursor-pointer"
      onClick={onClick}
      disabled={!enabled}
    >
      <svg
        className="embla__button__svg h-5 w-5  fill-purple-120"
        viewBox="137.718 -1.001 366.563 644"
      >
        <path d="M428.36 12.5c16.67-16.67 43.76-16.67 60.42 0 16.67 16.67 16.67 43.76 0 60.42L241.7 320c148.25 148.24 230.61 230.6 247.08 247.08 16.67 16.66 16.67 43.75 0 60.42-16.67 16.66-43.76 16.67-60.42 0-27.72-27.71-249.45-249.37-277.16-277.08a42.308 42.308 0 0 1-12.48-30.34c0-11.1 4.1-22.05 12.48-30.42C206.63 234.23 400.64 40.21 428.36 12.5z" />
      </svg>
    </button>
  );

  const NextButton = ({enabled, onClick}) => {
    return (
      <button
        className={` ${
          !enabled ? 'fill-gray-300' : 'fill-purple-120'
        } hidden absolute right-0 bottom-52 top-2/4 items-center justify-center rounded-full h-10 w-10 bg-white cursor-pointer`}
        onClick={onClick}
        disabled={!enabled}
      >
        <svg
          className={`embla__button__svg  h-5 w-5 fill-purple-120  ${
            !enabled ? 'fill-gray-300' : 'fill-purple-120'
          }`}
          viewBox="0 0 238.003 238.003"
        >
          <path d="M181.776 107.719L78.705 4.648c-6.198-6.198-16.273-6.198-22.47 0s-6.198 16.273 0 22.47l91.883 91.883-91.883 91.883c-6.198 6.198-6.198 16.273 0 22.47s16.273 6.198 22.47 0l103.071-103.039a15.741 15.741 0 0 0 4.64-11.283c0-4.13-1.526-8.199-4.64-11.313z" />
        </svg>
      </button>
    );
  };

  const onSelect = useCallback(() => {
    if (!embla) return;
    setSelectedIndex(embla.selectedScrollSnap());
    setPrevBtnEnabled(embla.canScrollPrev());
    setNextBtnEnabled(embla.canScrollNext());
  }, [embla, setSelectedIndex]);

  const handleOnClickMainLink = (e, link) => {
    e.preventDefault();

    if (link.title == 'Ofertas') {
      dataLayer.push({
        event: 'select_promotion', //Static data
        creative_name: 'Ofertas ', //Dynamic data
        creative_slot: 'ofertas', //Dynamic data
        promotion_name: 'Ofertas', //Dynamic data
      });
    }

    const findSubmenu = menu.menu.find(({title}) => title == link.title);
    if (findSubmenu.submenu) {
      setTitle(link.title == selectedTitle ? '' : link.title);
    } else {
      window.location = '/' + getPath(link);
    }
  };
  useEffect(() => {
    if (isOpen) {
      scrollPosition = window.scrollY;
      document.body.style.position = 'fixed';
    } else if (document.body.style.position) {
      document.body.style.position = '';
      window.scrollTo(0, scrollPosition);
    }
  }, [isOpen]);
  useEffect(() => {
    if (pending) {
      setIsOpen(false);
    }
  }, [pending]);
  return (
    <div className="lg:hidden">
      <OpenFocusTrap>
        {isOpen ? (
          <button
            type="button"
            className="toggle-btn toggle-close flex w-7 h-full"
            onClick={() => setIsOpen((previousIsOpen) => !previousIsOpen)}
          >
            <span className="sr-only">{isOpen ? 'Close' : 'Open'} Menu</span>
            <CloseIcon />
          </button>
        ) : (
          <button
            type="button"
            className="toggle-btn toggle-open flex w-7 h-full"
            onClick={() => setIsOpen((previousIsOpen) => !previousIsOpen)}
          >
            <span className="sr-only">{isOpen ? 'Close' : 'Open'} Menu</span>
            <svg
              width="30"
              height="18"
              viewBox="0 0 30 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 1H29M1 9H29M1 17H29"
                stroke="#5A66AD"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        {isOpen ? (
          <div className="mobile-nav fixed -left-0 top-20 w-full h-screen z-10 bg-gray-50">
            <div className="logo-wrapper">
              <Link to="/">
                <Image src={logo} width={129} height={36} alt="Logo" />
              </Link>
            </div>
            <div className="flex flex-wrap bg-white">
              <div className="w-full text-left">
                {menu
                  ? menu.menu.map((link) => {
                      return (
                        // eslint-disable-next-line jsx-a11y/anchor-is-valid
                        <a
                          href="#"
                          className={` ${
                            link.title === selectedTitle ? 'font-semibold' : ''
                          } mobile-nav-item librero-nav-item text-left text-md h-10  `}
                          onClick={(e) => {
                            handleOnClickMainLink(e, link);
                            e.preventDefault();
                          }}
                          key={link.title}
                        >
                          <span> {link.title}</span>

                          <ArrowRightIcon className="nav-icon" />
                        </a>
                      );
                    })
                  : ''}
              </div>
              {menu && menu?.menu
                ? menu.menu
                    .filter((link) => link.title == selectedTitle)
                    .map((item) => {
                      const collectionMenu = item?.collectionMenu
                        ? item.collectionMenu.map((collection) => {
                            const links = collection.links.map((link) => {
                              return (
                                <div key={link._key} className='item-menu-phone'>
                                  <Link
                                    className="relative"
                                    to={getPath(link)}
                                    onClick={() =>
                                      setIsOpen(
                                        (previousIsOpen) => !previousIsOpen,
                                      )
                                    }
                                  >
                                    {link.text}
                                    {link?.image ? (
                                      <div className="nf-icon rounded-full h-12 w-12 bg-white border-2 xs:border-1 border-purple-120 flex justify-center items-center absolute right-0 xs:top-0">
                                        <Image
                                          src={link?.image.asset.url}
                                          height={10}
                                          width={20}
                                        />
                                      </div>
                                    ) : (
                                      ''
                                    )}
                                  </Link>
                                </div>
                              );
                            });
                            return (
                              <li
                                key={collection._key}
                                className="nav-featured-item"
                              >
                                {links}
                              </li>
                            );
                          })
                        : null;

                      const submenu = item?.submenu ? (
                        item?.submenu.map((collection) => {
                          const links = collection?.links ? (
                            collection?.links.map((link) => (
                              <li key={link._key} className="nav-item">
                                <Link to={getPath(link)}>{link.text}</Link>
                              </li>
                            ))
                          ) : (
                            <></>
                          );

                          return (
                            <div key={collection._key} className="w-full">
                              <Link to={getPath(collection)}>
                                <h3 className="nav-column-header">
                                  {collection.title}
                                </h3>
                              </Link>
                              <ul>{links}</ul>
                            </div>
                          );
                        })
                      ) : (
                        <></>
                      );
                      const featured = (item) => {
                        return (
                          <div className="px-5 py-3">
                            <div className="">
                              <div className="flex flex-wrap justify-center items-center">
                                {item?.featuredImage?.mobileImage && (
                                  <div className="w-full relative nav-image-banner-mobile">
                                    <Image
                                      src={
                                        item?.featuredImage?.mobileImage.asset
                                          .url
                                      }
                                      height={100}
                                      width={420}
                                    />
                                    <p className="absolute left-5 top-0 w-8/12 text-white text-2xl font-semibold px-2 py-5">
                                      {item.featuredImage.text}
                                    </p>
                                    <div className="banner-btn absolute bottom-6 w-full flex justify-center">
                                      <Link
                                        to={getPath(item.featuredImage)}
                                        className="hover:font-bold rounded-full px-10 py-2  text-white font-semibold bg-purple-120 m-auto"
                                      >
                                        {item.featuredImage.buttonText}
                                      </Link>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      };
                      return (
                        <div
                          className="mobile-sub-menu pb-5 w-full absolute -top-0 bg-white h-full"
                          key={item._key}
                          onClick={() => {
                            setTitle('');
                          }}
                        >
                          <div className="logo-wrapper">
                            <Link to="/">
                              <Image
                                src={logo}
                                width={129}
                                height={36}
                                alt="Logo"
                              />
                            </Link>
                          </div>
                          <div className="bg-gray-75 py-4">
                            <div className="submenu-header w-full text-left px-5 relative">
                              <Image
                                src={arrowLeft}
                                width={13}
                                height={22}
                                alt="Go Back"
                              />
                              <span>{item.title}</span>
                              <Link
                                to={getPath(item)}
                                className="absolute right-5 view-all-mobile"
                              >
                                <p>Ver todo</p>
                              </Link>
                            </div>
                            <div className="w-full">{featured(item)} </div>
                            <PrevButton
                              onClick={scrollPrev}
                              enabled={prevBtnEnabled}
                            />
                            <NextButton
                              onClick={scrollNext}
                              enabled={nextBtnEnabled}
                            />
                            {collectionMenu && (
                              <div className="px-5 carousel_inner overflow-hidden">
                                <div ref={viewportRef}>
                                <a href="/#">
                                  <h3 class="nav-column-header">{item?.collectionMenu[0].title} &gt;</h3>
                                </a>
                                  <ul className="nav-featured-list-mobile embla__container">
                                    {collectionMenu}
                                  </ul>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-wrap flex-col text-left p-6 mobile-submenu">
                            <div className="w-full flex-wrap flex submenu">
                              {submenu}
                            </div>
                          </div>
                        </div>
                      );
                    })
                : ''}
            </div>
          </div>
        ) : null}
      </OpenFocusTrap>
    </div>
  );
}

function CloseIcon() {
  return (
    <svg
      width="8"
      height="8"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1 17L17 1M1 1L17 17"
        stroke="#5A66AD"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowRightIcon({className}) {
  return (
    <svg
      className={className}
      width="7"
      height="12"
      viewBox="0 0 7 12"
      fill="#5a66ad"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0.999762 12C0.743762 12 0.487762 11.902 0.292762 11.707C-0.0982383 11.316 -0.0982383 10.684 0.292762 10.293L4.58576 6.00001L0.292762 1.70701C-0.0982383 1.31601 -0.0982383 0.684006 0.292762 0.293006C0.683762 -0.0979941 1.31576 -0.0979941 1.70676 0.293006L6.70676 5.29301C7.09776 5.68401 7.09776 6.31601 6.70676 6.70701L1.70676 11.707C1.51176 11.902 1.25576 12 0.999762 12Z"
        fill="#5a66ad"
      />
    </svg>
  );
}
